import { ArrowLeft, Download, File, Folder, HardDrive, Lock, Monitor, RotateCcw, Search } from "lucide-react";
import { Fragment, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { useCatalogOccurrences, useCatalogSearch, useFiles, useInvalidateOrg, useMachine, useMachines, useSnapshots } from "../api/queries";
import type { RestoreRequest } from "../api/types";
import { API_MODE } from "../app/mode";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, PageHead, Panel } from "../ui/Common";
import { useToast } from "../ui/Toasts";
import { restoreMessages } from "./restore.i18n";

type Step = 1 | 2 | 3 | 4;
type TargetKind = RestoreRequest["target"]["kind"];

/** Caminho relativo à raiz do snapshot, sempre com "/". "" é a raiz. */
const joinRel = (dir: string, name: string) => (dir ? `${dir}/${name}` : name);

export function RestorePage() {
  const { orgId = "", machineId = "" } = useParams();
  const [params] = useSearchParams();
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const invalidate = useInvalidateOrg();
  const machine = useMachine(orgId, machineId);
  const snaps = useSnapshots(orgId, machineId);
  const t = useT(restoreMessages);
  const tc = useT(common);
  const f = useFormat();

  const [step, setStep] = useState<Step>(params.get("snapshot") ? 2 : 1);
  const [snapshotId, setSnapshotId] = useState<string | undefined>(params.get("snapshot") ?? undefined);
  const [dir, setDir] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetKind, setTargetKind] = useState<TargetKind>("original");
  const [targetPath, setTargetPath] = useState("");
  const [destId, setDestId] = useState("");
  const allMachines = useMachines(orgId);
  const [conflict, setConflict] = useState<RestoreRequest["conflict"]>("keep_both");
  const [busy, setBusy] = useState(false);
  // Busca no catálogo (ADR 0036): pelo nome, em todos os pontos da máquina.
  const [query, setQuery] = useState("");
  const [term, setTerm] = useState("");
  const [hit, setHit] = useState<{ dir: string; name: string } | undefined>();

  // A restauração navega arquivos; imagem de disco é volume bruto e sai pela
  // aba de imagens da máquina (ADR 0022).
  const points = snaps.data?.filter((s) => s.kind !== "image" && s.kind !== "database") ?? [];
  const snapshot = points.find((s) => s.id === snapshotId) ?? (snapshotId ? undefined : points[0]);
  const files = useFiles(orgId, step === 2 ? snapshot?.id : undefined, dir);
  const search = useCatalogSearch(orgId, machineId, step === 2 ? term : "");
  const occurrences = useCatalogOccurrences(orgId, machineId, hit?.dir, hit?.name);

  if (machine.isPending || snaps.isPending) return <div className="panel"><LoadingRows rows={8} /></div>;
  if (machine.isError) return <ErrorState error={machine.error} />;
  const m = machine.data;
  const sep = m.os === "windows" ? "\\" : "/";
  const display = (rel: string) => {
    const root = snapshot?.sourcePath ?? "";
    if (!rel) return root;
    return root.endsWith(sep) ? root + rel.split("/").join(sep) : root + sep + rel.split("/").join(sep);
  };
  const crumbs = dir ? dir.split("/").map((part, i, all) => ({ label: part, value: all.slice(0, i + 1).join("/") })) : [];

  function chooseSnapshot(id: string) {
    if (id !== snapshotId) {
      setSelected(new Set());
      setDir("");
    }
    setSnapshotId(id);
  }

  function closeSearch() {
    setTerm("");
    setQuery("");
    setHit(undefined);
  }

  /** Abre a pasta do item achado no ponto escolhido. */
  function openHit(id: string, dirPath: string) {
    chooseSnapshot(id);
    setDir(dirPath);
    closeSearch();
  }

  function toggle(p: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  }

  function download() {
    if (!snapshot) return;
    if (API_MODE === "mock") {
      toast("ok", t.mockZip(selected.size));
      return;
    }
    const q = new URLSearchParams();
    for (const p of selected) q.append("path", p);
    window.location.assign(`/api/v1/orgs/${encodeURIComponent(orgId)}/snapshots/${encodeURIComponent(snapshot.id)}/download?${q}`);
    toast("ok", t.downloadStarted);
  }

  async function start() {
    if (!snapshot) return;
    if (targetKind === "download") {
      download();
      return;
    }
    setBusy(true);
    try {
      await api.createRestore(orgId, {
        snapshotId: snapshot.id,
        paths: [...selected],
        target: targetKind === "other_machine"
          ? { kind: "other_machine", machineId: destId, path: targetPath }
          : { kind: "original", ...(targetPath ? { path: targetPath } : {}) },
        conflict,
      });
      invalidate(orgId);
      toast("ok", t.restoreSent);
      navigate(`/o/${orgId}/activity`);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.restoreFailed);
    } finally {
      setBusy(false);
    }
  }

  const destinations = (allMachines.data ?? []).filter((x) => x.id !== m.id && x.status !== "revoked");
  const dest = destinations.find((x) => x.id === destId);
  const canNext = step === 1 ? Boolean(snapshot) : step === 2 ? selected.size > 0 : step === 3 && targetKind === "other_machine" ? Boolean(dest) && targetPath.trim() !== "" : true;
  const targetName =
    targetKind === "download" ? t.targetDownload :
    targetKind === "other_machine" && dest ? t.targetPath(dest.hostname, targetPath) :
    targetPath ? t.targetPath(m.hostname, targetPath) : t.targetOriginal(m.hostname);

  return (
    <>
      <PageHead
        eyebrow={t.eyebrow}
        title={<span className="mono" style={{ fontFamily: "var(--font-mono)", fontStretch: "100%", fontWeight: 500 }}>{m.hostname}</span>}
        lede={t.lede}
        actions={<Link className="btn" to={`/o/${orgId}/machines/${encodeURIComponent(machineId)}`}><ArrowLeft aria-hidden="true" /> {t.backToMachine}</Link>}
      />

      <ol className="steps" aria-label={t.stepsLabel}>
        {t.steps.map((n, i) => (
          <li key={n} data-state={i + 1 < step ? "done" : i + 1 === step ? "current" : "todo"} aria-current={i + 1 === step ? "step" : undefined}>{n}</li>
        ))}
      </ol>

      {step === 1 && (
        <Panel title={t.whenTitle} sub={t.whenSub(points.length)} bodyless>
          {snaps.isError ? <div className="panel-body"><ErrorState error={snaps.error} /></div> : points.length === 0 ? (
            <div className="empty">{t.noSnapshots}</div>
          ) : (
            <div className="snap-list" role="list">
              {points.map((s) => (
                <button key={s.id} type="button" className="snap" role="listitem" aria-pressed={snapshot?.id === s.id} onClick={() => chooseSnapshot(s.id)}>
                  <span className="when">{t.when(f.weekdayDate(s.startedAt), f.time(s.startedAt))}</span>
                  <span className="num ink-2">{f.bytes(s.totalBytes)}</span>
                  <span className="sid">{s.sourcePath} · {s.id.slice(0, 10)}{s.status === "partial" ? ` · ${t.partial}` : ""}</span>
                  <span />
                  <span className="tags">
                    {s.retention.map((r) => <span key={r} className="tag">{tc.retentionKind[r]}</span>)}
                    {s.immutableUntil && <span className="tag"><Lock aria-hidden="true" />{t.immutableUntil(f.fullDate(s.immutableUntil))}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Panel>
      )}

      {step === 2 && snapshot && (
        <Panel
          title={t.whatTitle}
          sub={t.whatSub(f.weekdayDate(snapshot.startedAt), selected.size)}
          actions={selected.size > 0 && <button className="btn btn-sm btn-ghost" type="button" onClick={() => setSelected(new Set())}>{t.clearSelection}</button>}
          bodyless
        >
          <form className="panel-body" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }} onSubmit={(e) => { e.preventDefault(); setHit(undefined); setTerm(query.trim()); }}>
            <input id="rest-busca" className="input" style={{ flex: "1 1 240px" }} aria-label={t.searchLabel} placeholder={t.searchPlaceholder} value={query} onChange={(e) => setQuery(e.target.value)} />
            <button className="btn" type="submit" disabled={query.trim() === ""}><Search aria-hidden="true" /> {t.searchButton}</button>
            {term && <button className="btn btn-ghost" type="button" onClick={closeSearch}>{t.closeSearch}</button>}
            <span className="hint" style={{ flexBasis: "100%" }}>{t.searchHint}</span>
          </form>
          {term ? (
            search.isPending ? <LoadingRows rows={4} /> : search.isError ? <div className="panel-body"><ErrorState error={search.error} /></div> : search.data?.length === 0 ? (
              <div className="empty">{t.searchNone}</div>
            ) : (
              <div className="table-wrap">
                <div className="panel-body muted" style={{ paddingTop: 0 }}>
                  {t.searchResults(search.data?.length ?? 0)}{(search.data?.length ?? 0) >= 100 ? ` ${t.searchLimit}` : ""}
                </div>
                <table className="files">
                  <tbody>
                    {search.data?.map((h) => {
                      const open = hit?.dir === h.dirObjectId && hit?.name === h.name;
                      const where = open && occurrences.data ? points.filter((p) => occurrences.data.snapshotIds.includes(p.id)) : [];
                      return (
                        <Fragment key={`${h.dirObjectId}/${h.name}`}>
                          <tr>
                            <td>
                              <span className="name">
                                {h.kind === "dir" ? <Folder aria-hidden="true" /> : <File aria-hidden="true" />}
                                <button type="button" aria-expanded={open} onClick={() => setHit(open ? undefined : { dir: h.dirObjectId, name: h.name })}>{h.name}</button>
                              </span>
                              <div className="muted mono" style={{ fontSize: "0.85em" }}>{display(h.dirPath)}</div>
                            </td>
                            <td className="num muted" style={{ textAlign: "right", whiteSpace: "nowrap" }}>{h.kind === "dir" ? t.folder : f.bytes(h.size)}</td>
                            <td className="num muted" style={{ whiteSpace: "nowrap" }}>{f.dateTime(h.modifiedAt)}</td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={3}>
                                {occurrences.isPending ? <LoadingRows rows={1} /> : occurrences.isError ? <ErrorState error={occurrences.error} /> : where.length === 0 ? (
                                  <span className="muted">{t.occurrencesNone}</span>
                                ) : (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                                    <span className="muted">{t.occurrencesTitle}</span>
                                    {where.map((p) => (
                                      <button key={p.id} type="button" className="btn btn-sm" onClick={() => openHit(p.id, occurrences.data?.dirPath ?? h.dirPath)}>
                                        {t.when(f.weekdayDate(p.startedAt), f.time(p.startedAt))}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <>
              <div className="pathbar">
                <HardDrive size={14} aria-hidden="true" />
                <button type="button" onClick={() => setDir("")}>{snapshot.sourcePath}</button>
                {crumbs.map((c) => (
                  <span key={c.value} style={{ display: "inline-flex", gap: 4 }}>
                    <span className="muted">{sep}</span>
                    <button type="button" onClick={() => setDir(c.value)}>{c.label}</button>
                  </span>
                ))}
              </div>
              {snapshot.catalog === "pending" && <div className="panel-body hint">{t.catalogPending}</div>}
              {snapshot.catalog === "failed" && <div className="panel-body hint">{t.catalogFailed}</div>}
              {files.isPending ? <LoadingRows rows={5} /> : files.isError ? <div className="panel-body"><ErrorState error={files.error} /></div> : files.data?.length === 0 ? (
                <div className="empty">{t.emptyFolder}</div>
              ) : (
                <div className="table-wrap">
                  <table className="files">
                    <tbody>
                      {files.data?.map((entry) => {
                        const rel = joinRel(dir, entry.name);
                        return (
                          <tr key={entry.name}>
                            <td style={{ width: 36 }}>
                              <input type="checkbox" aria-label={t.select(entry.name)} checked={selected.has(rel)} onChange={() => toggle(rel)} id={`sel-${rel}`} />
                            </td>
                            <td>
                              <span className="name">
                                {entry.kind === "dir" ? <Folder aria-hidden="true" /> : <File aria-hidden="true" />}
                                {entry.kind === "dir" ? <button type="button" onClick={() => setDir(rel)}>{entry.name}</button> : <span>{entry.name}</span>}
                              </span>
                            </td>
                            <td className="num muted" style={{ textAlign: "right", whiteSpace: "nowrap" }}>{entry.kind === "dir" ? t.folder : f.bytes(entry.size)}</td>
                            <td className="num muted" style={{ whiteSpace: "nowrap" }}>{f.dateTime(entry.modifiedAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      {step === 3 && (
        <Panel title={t.whereTitle}>
          <div style={{ display: "grid", gap: 18 }}>
            <div className="choice-grid" role="group" aria-label={t.targetGroup}>
              <button type="button" className="choice" aria-pressed={targetKind === "original"} onClick={() => setTargetKind("original")}>
                <strong><RotateCcw aria-hidden="true" />{t.thisMachine}</strong>
                <span>{t.thisMachineHint(m.hostname)}</span>
              </button>
              <button type="button" className="choice" aria-pressed={targetKind === "download"} onClick={() => setTargetKind("download")}>
                <strong><Download aria-hidden="true" />{t.zip}</strong>
                <span>{t.zipHint}</span>
              </button>
              <button type="button" className="choice" aria-pressed={targetKind === "other_machine"} onClick={() => { setTargetKind("other_machine"); setTargetPath(""); }}>
                <strong><Monitor aria-hidden="true" />{t.otherMachine}</strong>
                <span>{t.otherMachineHint}</span>
              </button>
            </div>

            {targetKind !== "download" && (
              <>
                {targetKind === "other_machine" && (
                  <div className="field">
                    <label htmlFor="rest-destino">{t.destinationMachine}</label>
                    <select id="rest-destino" className="select" value={destId} onChange={(e) => setDestId(e.target.value)}>
                      <option value="">{t.chooseMachine}</option>
                      {destinations.map((x) => <option key={x.id} value={x.id}>{x.hostname}{x.online ? "" : ` · ${t.offline}`}</option>)}
                    </select>
                    <span className="hint">{t.otherMachineSecurity}</span>
                  </div>
                )}
                <div className="field">
                  <label htmlFor="rest-caminho">{t.targetFolder}</label>
                  <input id="rest-caminho" className="input mono"
                    placeholder={targetKind === "other_machine" ? (dest?.os === "windows" ? "C:\\Restaurado" : "/home/usuario/restaurado") : t.targetFolderPlaceholder(snapshot?.sourcePath ?? t.originalLocation)}
                    value={targetPath} onChange={(e) => setTargetPath(e.target.value)} />
                  <span className="hint">{targetKind === "other_machine" ? t.otherMachineFolderHint : t.targetFolderHint(snapshot?.sourcePath ?? "")}</span>
                </div>
                <div className="field">
                  <span className="label" id="conflito-label">{t.ifExists}</span>
                  <div className="segmented" role="group" aria-labelledby="conflito-label">
                    <button type="button" aria-pressed={conflict === "keep_both"} onClick={() => setConflict("keep_both")}>{t.keepBoth}</button>
                    <button type="button" aria-pressed={conflict === "skip"} onClick={() => setConflict("skip")}>{t.skip}</button>
                    <button type="button" aria-pressed={conflict === "overwrite"} onClick={() => setConflict("overwrite")}>{t.overwrite}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Panel>
      )}

      {step === 4 && snapshot && (
        <Panel title={t.reviewTitle}>
          <dl className="kv">
            <dt>{t.reviewPoint}</dt><dd>{t.when(f.weekdayDate(snapshot.startedAt), f.time(snapshot.startedAt))} <span className="mono muted">{snapshot.id.slice(0, 10)}</span></dd>
            <dt>{t.reviewItems}</dt><dd>{[...selected].map((p) => <div key={p} className="mono">{display(p)}</div>)}</dd>
            <dt>{t.reviewTarget}</dt><dd>{targetName}</dd>
            {targetKind !== "download" && (<><dt>{t.reviewConflicts}</dt><dd>{t.conflictLabel[conflict]}</dd></>)}
          </dl>
          {conflict === "overwrite" && targetKind !== "download" && (
            <div className="callout warn" style={{ marginTop: 14 }}>
              <Lock aria-hidden="true" />
              <span>{t.overwriteWarning}</span>
            </div>
          )}
          {targetKind === "other_machine" && dest && !dest.online && (
            <div className="callout warn" style={{ marginTop: 14 }}>
              <Monitor aria-hidden="true" />
              <span>{t.offlineWarning(dest.hostname)}</span>
            </div>
          )}
          {targetKind === "original" && !m.online && (
            <div className="callout warn" style={{ marginTop: 14 }}>
              <Monitor aria-hidden="true" />
              <span>{t.offlineWarning(m.hostname)}</span>
            </div>
          )}
        </Panel>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <button className="btn" type="button" disabled={step === 1} onClick={() => setStep((s) => (s - 1) as Step)}>{tc.back}</button>
        {step < 4 ? (
          <button className="btn btn-primary" type="button" disabled={!canNext} onClick={() => { if (step === 1 && snapshot) chooseSnapshot(snapshot.id); setStep((s) => (s + 1) as Step); }}>
            {t.continue}
          </button>
        ) : (
          <button className="btn btn-primary" type="button" disabled={busy} onClick={start}>
            {targetKind === "download" ? <><Download aria-hidden="true" /> {t.downloadZip}</> : <><RotateCcw aria-hidden="true" /> {t.startRestore}</>}
          </button>
        )}
      </div>
    </>
  );
}
